/**
 * The association's shared knowledge pool for EVERY lender questionnaire.
 *
 * Each bank's form is a view over the same facts about the project — Fannie
 * Mae 1076 asks most of them, and each bank adds a few of its own. The pool is
 * keyed by field NAME: a bank spec that names a field `fin_litigation` reads
 * and writes the same answer the 1076 does. This module is the registry the
 * Project Details tab renders: the 1076's operations fields, plus the
 * questions the other bank forms ask that the 1076 does not.
 *
 * Adding a bank: reuse names from here wherever the question means the same
 * thing; add genuinely new questions to EXTRA_FIELDS (never to the 1076 spec —
 * that would put them on the 1076 form itself).
 *
 * Mapped 3-Sep-2026 from PCB Bank "Full Review" (30 questions) and Mid America
 * "Condo 2-4 Unit" (11 questions) against the 166 of the 1076.
 */

import { projectDetailFields } from "../fannie-mae-1076/spec.mjs";

const STEP = "Additional bank questions";
const YN = ["Yes", "No"];

/** Questions other banks ask that the 1076 does not. Same FlatField shape. */
export const EXTRA_FIELDS = [
  // --- Project identity -----------------------------------------------------
  {
    name: "phase_number",
    label: "Phase the subject property is located in, if the project is phased",
    type: "text",
    sectionTitle: "Project identity",
  },
  {
    name: "mgmt_professional",
    label: "Is the project managed by a professional management company?",
    type: "select",
    options: YN,
    sectionTitle: "Project identity",
  },

  // --- Eligibility ----------------------------------------------------------
  {
    name: "elig_hotel_operations",
    label: "Does the project operate as a hotel, motel, or resort, renting units on a daily basis?",
    type: "select",
    options: YN,
    sectionTitle: "Eligibility",
  },
  {
    name: "elig_rental_pooling",
    label: "Does the project have mandatory or voluntary rental-pooling arrangements?",
    type: "select",
    options: YN,
    sectionTitle: "Eligibility",
  },
  {
    name: "elig_non_real_estate",
    label: "Does the project have non-real-estate property, such as houseboats?",
    type: "select",
    options: YN,
    sectionTitle: "Eligibility",
  },
  {
    name: "elig_multi_dwelling_units",
    label: "Does the project have any multi-dwelling units (including lockout units)?",
    type: "select",
    options: YN,
    sectionTitle: "Eligibility",
  },
  {
    name: "elig_timeshare",
    label: "Is the project a timeshare or a segmented-ownership project?",
    type: "select",
    options: YN,
    sectionTitle: "Eligibility",
  },
  {
    name: "elig_live_work_units",
    label: "Does the project have any live-work units?",
    type: "select",
    options: YN,
    sectionTitle: "Eligibility",
  },
  {
    name: "elig_environmental_factors",
    label: "Are there any adverse environmental factors affecting the project as a whole or its individual units?",
    type: "select",
    options: YN,
    sectionTitle: "Eligibility",
  },
  {
    name: "elig_business_income_over_10pct",
    label: "If the HOA owns business operations: does it receive more than 10% of its budgeted income from them?",
    type: "select",
    options: YN,
    sectionTitle: "Eligibility",
  },
  {
    name: "elig_business_income_over_15pct",
    label: "Does the HOA receive more than 15% of its budgeted income from amenity leases or from leasing units acquired through foreclosure?",
    type: "select",
    options: YN,
    sectionTitle: "Eligibility",
  },
  {
    name: "elig_financing_concessions",
    label: "(New projects only) Are financing structures being offered, such as builder or developer contributions, sales concessions, HOA assessments, or payment abatements?",
    type: "select",
    options: YN,
    sectionTitle: "Eligibility",
  },
  {
    name: "conversion_original_use",
    label: "If the project is a conversion: what was the original occupancy use or purpose?",
    type: "text",
    sectionTitle: "Eligibility",
  },

  // --- Financial ------------------------------------------------------------
  {
    name: "fin_monthly_dues",
    label: "Monthly HOA dues for a typical unit ($)",
    type: "text",
    sectionTitle: "Financial",
  },
  {
    name: "fin_delinquent_30_days",
    label: "How many units are 30 or more days delinquent on their HOA dues?",
    type: "text",
    help: "The 1076 asks at 60 days; Mid America asks at 30.",
    sectionTitle: "Financial",
  },

  // --- Insurance ------------------------------------------------------------
  {
    name: "ins_hoa_named_insured",
    label: "Is the HOA the named insured on the master insurance policy?",
    type: "select",
    options: YN,
    sectionTitle: "Insurance",
  },
  {
    name: "ins_replacement_coverage",
    label: "Are the common elements insured to 100% replacement cost?",
    type: "select",
    options: YN,
    sectionTitle: "Insurance",
  },
  {
    name: "ins_coverage_amount",
    label: "Master policy coverage amount ($)",
    type: "text",
    sectionTitle: "Insurance",
  },
  {
    name: "ins_deductible",
    label: "Master policy deductible ($)",
    type: "text",
    sectionTitle: "Insurance",
  },
  {
    name: "ins_policy_expiration",
    label: "Master policy expiration date",
    type: "date",
    sectionTitle: "Insurance",
  },
  {
    name: "ins_flood_in_force",
    label: "If units or common elements are in a flood zone: is flood insurance in force?",
    type: "select",
    options: YN,
    sectionTitle: "Insurance",
  },
  {
    name: "ins_flood_80pct",
    label: "Does the flood coverage equal at least 80% of replacement cost?",
    type: "select",
    options: YN,
    sectionTitle: "Insurance",
  },
  {
    name: "ins_flood_max_federal",
    label: "Or, is the flood coverage the maximum available under the federal flood program?",
    type: "select",
    options: YN,
    sectionTitle: "Insurance",
  },
  {
    name: "ins_general_liability",
    label: "Is the HOA insured for general liability?",
    type: "select",
    options: YN,
    sectionTitle: "Insurance",
  },
  {
    name: "ins_liability_per_occurrence",
    label: "General liability amount per occurrence ($)",
    type: "text",
    help: "Mid America's guide wants $1 million per occurrence.",
    sectionTitle: "Insurance",
  },
  {
    name: "ins_walls_in_coverage",
    label: "Does the HOA provide hazard coverage for the interior (walls-in) of the units?",
    type: "select",
    options: YN,
    sectionTitle: "Insurance",
  },
  {
    name: "ins_notice_days",
    label: "Minimum days of written notice to the HOA before a substantial policy change or cancellation",
    type: "text",
    sectionTitle: "Insurance",
  },
].map((f, i) => ({
  by: "operations",
  stepTitle: STEP,
  stepIndex: 100,
  sectionIndex: ["Project identity", "Eligibility", "Financial", "Insurance"].indexOf(f.sectionTitle),
  fieldIndex: i,
  ...f,
}));

/** Every question the Project Details tab shows: the 1076's, then the extras. */
export function sharedProjectDetailFields() {
  return [...projectDetailFields(), ...EXTRA_FIELDS];
}
